/**
 * FleetOperationsReportPage — `/reports/fleet-ops`.
 *
 * Wired to GET /v1/reports/fleet-ops?period= on service-reporting-api.
 * Note: fleet/vehicle data is not yet in SourceReaders — this report returns
 * stub data with a stub=true flag until loadVehicles() is wired.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Truck } from 'lucide-react';

import { getFleetOpsReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

export function FleetOperationsReportPage(): JSX.Element {
  const report = findPreBuiltReport('fleet-ops');
  const [period, setPeriod] = useState<string>('THIS_MONTH');

  const query = useQuery({
    queryKey: ['pre-built', 'fleet-ops', period],
    queryFn: () => getFleetOpsReport(period),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period"
              className="px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Total vehicles" value={data ? data.totalVehicles.toLocaleString('en-US') : '—'} hint="Delivery" icon={Truck} />
        <KPICard title="Active vehicles" value={data ? data.activeVehicles.toLocaleString('en-US') : '—'} hint="Delivery" />
        <KPICard title="Utilisation %" value={data ? `${data.utilizationPct}%` : '—'} hint="active / total" />
        <KPICard title="Maintenance flagged" value={data ? data.maintenanceFlagged.toLocaleString('en-US') : '—'} hint="Delivery" tone={data && data.maintenanceFlagged > 0 ? 'warning' : undefined} />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readFleetOps</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Vehicle breakdown</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">
              No fleet vehicles found for the period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Vehicle</th>
                    <th className="text-left px-4 py-2">Driver</th>
                    <th className="text-right px-4 py-2">Trips</th>
                    <th className="text-right px-4 py-2">Utilisation %</th>
                    <th className="text-right px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((row) => (
                    <tr key={row.vehicleId}>
                      <td className="px-4 py-2 font-mono text-xs">{row.vehicleId}</td>
                      <td className="px-4 py-2 font-mono text-xs">{row.driverId}</td>
                      <td className="px-4 py-2 text-right text-xs">{row.trips.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs">{row.utilization}%</td>
                      <td className="px-4 py-2 text-right text-xs">
                        {row.maintained ? (
                          <span className="text-nipost-green font-medium">OK</span>
                        ) : (
                          <span className="text-amber-700 font-medium">Maintenance</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
